import React, { useState, useRef, useEffect } from 'react';
import axios from "axios";
import config from "../common/Config";
import { toast } from "sonner";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Grid2,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Checkbox,
  Select as MuiSelect,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { format, parse } from 'date-fns';
import { Iconify } from '../Minimal/src/components/iconify/iconify';
import productSplitService from '../../services/ProductSplitService';
import '../common/commonStyles.css';
import { useNavigate } from 'react-router-dom';

const normalizeReturnUnit = (type) => {
  const value = String(type ?? '').trim().toLowerCase();
  if (value === 'piece' || value === 'pieces' || value === 'p') return 'piece';
  return 'box';
};


const getPiecesPerBox = (row, customerId) => {
  const candidates = [
    row?.noOfPieceInBox,
    row?.pack_size,
    row?.unit_per_box,
    row?.pieces_per_box,
    row?.qty_in_box,
    row?.pack,
  ];
  if (customerId && row?.product_id) {
    const splitData = productSplitService.getSplitData(customerId, row.product_id);
    if (splitData?.noOfPieceInBox) candidates.unshift(splitData.noOfPieceInBox);
  }
  for (let i = 0; i < candidates.length; i += 1) {
    const n = parseFloat(candidates[i]);
    if (Number.isFinite(n) && n > 1) return n;
  }
  for (let i = 0; i < candidates.length; i += 1) {
    const n = parseFloat(candidates[i]);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 1;
};

const resolveEffectiveReturnType = (row, selectedReturnType) => {
  if (String(row?.split ?? '') === '1') return 'piece';
  return normalizeReturnUnit(selectedReturnType);
};

const qtyToPieces = (qty, unit, piecesPerBox) => {
  const n = Number(qty) || 0;
  return normalizeReturnUnit(unit) === 'piece' ? n : n * piecesPerBox;
};

const getAlreadyReturnedPieces = (orderLineId, returnRows, customerId) => {
  return (returnRows || []).reduce((sum, r) => {
    const lineId = r.order_line_id ?? r.sale_item_id ?? r.order_id ?? r.id;
    if (String(lineId) !== String(orderLineId)) return sum;
    return sum + qtyToPieces(r.return_qty, r.return_type, getPiecesPerBox(r, customerId));
  }, 0);
};

const ORDER_OPTION_LIMIT = 100;
const PRODUCT_OPTION_LIMIT = 100;
const PRODUCT_OPTION_INITIAL = 50;

const normalizeReturnDetailsRows = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.return_details)) return data.return_details;
  if (Array.isArray(data?.product_details)) return data.product_details;
  return [];
};

const isReturnDetailsUnavailable = (data) => {
  if (data == null) return true;
  if (data.success === false || data.success === 0 || data.success === '0') return true;
  const message = String(data.message ?? data.msg ?? '').toLowerCase();
  if (message.includes('not available') || message.includes('no data') || message.includes('not found')) {
    return true;
  }
  return normalizeReturnDetailsRows(data).length === 0;
};

const getMaxReturnQtyInUnit = (row, returnType, returnRows, customerId) => {
  const piecesPerBox = getPiecesPerBox(row, customerId);
  const orderPieces = qtyToPieces(row.quantity, row.order_type, piecesPerBox);
  const remainingPieces = Math.max(
    0,
    orderPieces - getAlreadyReturnedPieces(row.id, returnRows, customerId)
  );
  if (normalizeReturnUnit(returnType) === 'piece') {
    return remainingPieces;
  }
  return piecesPerBox > 0 ? remainingPieces / piecesPerBox : 0;
};

const mapReturnDetailRow = (productData, customerId) => {
  if (customerId && productData.product_id) {
    productSplitService.storeSplitData(customerId, productData.product_id, productData);
  }
  const splitCache = customerId
    ? productSplitService.getSplitData(customerId, productData.product_id)
    : null;
  return {
    id: productData.id,
    reference_no: productData.reference_no,
    pack: productData.pack,
    noOfPieceInBox:
      productData.noOfPieceInBox ??
      productData.pack_size ??
      productData.unit_per_box ??
      splitCache?.noOfPieceInBox,
    split: productData.split,
    invoice_date: productData.invoice_date,
    product_id: productData.product_id,
    quantity: productData.quantity,
    unit_price: productData.unit_price,
    order_type: productData.order_type,
    item_tax: productData.item_tax,
    subtotal: productData.subtotal,
  };
};

const AddReturn = () => {
  const [selectedDate, setSelectedDate] = useState('');
  const [noteInfo, setNoteInfo] = useState('');
  const [customeId, setCustomeId] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerData, setCustomerData] = useState([]);
  const [returnTableRows, setReturnTableRows] = useState([]);
  const accessToken = localStorage.getItem('accessToken');
  const token = localStorage.getItem('token');
  const company_tax_type = localStorage.getItem('company_tax_type');
  const user_id = localStorage.getItem('userId');
  const [returnAddMode, setReturnAddMode] = useState('invoice');
  const [orderOptions, setOrderOptions] = useState([]);
  const [allOrderOptions, setAllOrderOptions] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderInputValue, setOrderInputValue] = useState('');
  const [bulkReturnReason, setBulkReturnReason] = useState('unwanted');
  const [isLoadingOrderReturn, setIsLoadingOrderReturn] = useState(false);
  const [productOptions, setProductOptions] = useState([]);
  const [productValue, setProductValue] = useState(null);
  const [productInputValue, setProductInputValue] = useState('');
  const [tableRows, setTableRows] = useState([]);
  const [checkedValues, setCheckedValues] = useState([]);
  const [returnQtyValues, setReturnQtyValues] = useState({});
  const [returnQtyErrors, setReturnQtyErrors] = useState({});
  const [returnReasonValues, setReturnReasonValues] = useState({});
  const [returnReasonErrors, setReturnReasonErrors] = useState({});
  const [returnTypeValues, setReturnTypeValues] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });
  const customerSearchTimerRef = useRef(null);
  const productCatalogRef = useRef([]);
  const productOptionsRef = useRef([]);
  const productListRequestRef = useRef(0);
  const returnTableRowsRef = useRef([]);
  const orderReturnRequestRef = useRef(0);
  const returnDetailsByInvoiceRef = useRef(new Map());
  const navigate = useNavigate();

  useEffect(() => {
    returnTableRowsRef.current = returnTableRows;
  }, [returnTableRows]);
  const handleDateChange = (date) => {
    if (date) {
      const formattedDate = format(date, 'dd-MM-yyyy');
      setSelectedDate(formattedDate);
    } else {
      setSelectedDate('');
    }
  };

  const getDateValue = () => {
    if (!selectedDate) return null;
    try {
      return parse(selectedDate, 'dd-MM-yyyy', new Date());
    } catch {
      return null;
    }
  };

  const handleNoteChange = (event) => {
    setNoteInfo(event.target.value);
  };

  const filterOrderOptions = (inputValue) => {
    const query = String(inputValue ?? '').trim().toLowerCase();
    if (!query) {
      setOrderOptions(allOrderOptions.slice(0, ORDER_OPTION_LIMIT));
      return;
    }
    setOrderOptions(
      allOrderOptions
        .filter((option) => (option.label || '').toLowerCase().includes(query))
        .slice(0, ORDER_OPTION_LIMIT)
    );
  };

  const filterProductOptions = (inputValue) => {
    const query = String(inputValue ?? '').trim().toLowerCase();
    const allOptions = productOptionsRef.current;
    if (!query) {
      setProductOptions(allOptions.slice(0, PRODUCT_OPTION_INITIAL));
      return;
    }
    setProductOptions(
      allOptions
        .filter((option) => (option.label || '').toLowerCase().includes(query))
        .slice(0, PRODUCT_OPTION_LIMIT)
    );
  };

  const resetProductSelectionState = () => {
    setProductOptions([]);
    setProductValue(null);
    setProductInputValue('');
    productOptionsRef.current = [];
    setTableRows([]);
    setCheckedValues([]);
    setReturnQtyValues({});
    setReturnQtyErrors({});
    setReturnReasonValues({});
    setReturnReasonErrors({});
    setReturnTypeValues({});
  };

  const resetOrderSelectionState = () => {
    setOrderOptions([]);
    setAllOrderOptions([]);
    setSelectedOrder(null);
    setOrderInputValue('');
  };

  const handleReturnAddModeChange = (mode) => {
    setReturnAddMode(mode);
    if (mode === 'invoice') {
      resetProductSelectionState();
    } else {
      resetOrderSelectionState();
    }
  };

  const handleCustomerInputChange = (inputValue) => {
    if (customerSearchTimerRef.current) {
      clearTimeout(customerSearchTimerRef.current);
    }
    const trimmed = String(inputValue ?? '').trim();
    if (trimmed.length < 2) {
      setCustomerData([]);
      return;
    }
    customerSearchTimerRef.current = setTimeout(() => {
      fetchCustomerNameOptions(trimmed);
    }, 300);
  };

  const fetchCustomerNameOptions = async (inputValue) => {
    if (inputValue.length < 2) {
      return;
    }

    const formData = new FormData();
    formData.append('pattern', inputValue);
    formData.append('platform', 'web');
    formData.append('authorization', accessToken);

    const apiSalesRapUrl = `${config.API_URL}/ws-get-customer-list`;
    try {
      const response = await fetch(apiSalesRapUrl, {
        method: 'POST',
        headers: {},
        body: formData,
      });

      const data = await response.json();

      const formattedOptions = Array.isArray(data.customer_details)
        ? data.customer_details.map(item => ({
          label: item.name,
          value: item.id,
        }))
        : [];

      setCustomerData(formattedOptions);
    } catch (error) {
      toast.error('Error fetching customer options');
    }
  };

  const handleCustomerChange = (selectedOption) => {
    if (selectedOption) {
      setSelectedCustomer(selectedOption);
      setCustomeId(selectedOption.value);
      productCatalogRef.current = [];
      resetOrderSelectionState();
      resetProductSelectionState();
      returnDetailsByInvoiceRef.current.clear();
      fetchProductList(selectedOption);
      fetchCustomerOrders(selectedOption);
      fetchReturnProductInfo(selectedOption);
    } else {
      setSelectedCustomer(null);
      setCustomeId('');
      productCatalogRef.current = [];
      resetOrderSelectionState();
      resetProductSelectionState();
      returnDetailsByInvoiceRef.current.clear();
      setReturnTableRows([]);
    }
  };

  const fetchCustomerOrders = async (selectedOption) => {
    const formData = new FormData();
    formData.append('customerId', selectedOption.value);
    formData.append('showDueInvoicesOnly', 'N');
    formData.append('authorization', `Bearer ${token}`);

    try {
      const response = await fetch(`${config.API_URL}/ws-get-previous-invoice-list`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch invoices');
      }

      const data = await response.json();
      const invoices = Array.isArray(data?.invoice_details) ? data.invoice_details : [];
      const options = invoices.map((inv) => {
        const referenceNo = String(inv.reference_no || inv.invoice_no || inv.ids || '').trim();
        return {
          label: referenceNo || 'N/A',
          value: String(inv.ids || inv.sale_id || inv.id || referenceNo),
          reference_no: referenceNo,
          sale_id: inv.ids || inv.sale_id || inv.id,
          invoice_date: inv.invoice_date || inv.date || '',
        };
      });
      setAllOrderOptions(options);
      setOrderOptions(options.slice(0, ORDER_OPTION_LIMIT));
    } catch (error) {
      setAllOrderOptions([]);
      setOrderOptions([]);
      toast.error('Error fetching customer invoices');
    }
  };

  const fetchProductList = async (selectedOption) => {
    const requestId = productListRequestRef.current + 1;
    productListRequestRef.current = requestId;
    const formData = new FormData();
    formData.append("platform", 'web');
    formData.append("authorization", `Bearer ${accessToken}`);
    formData.append("customer_id", selectedOption.value);

    const apiUrl = `${config.API_URL}/get_return_productslist`;
    try {
      const response = await axios.post(apiUrl, formData, {});

      if (productListRequestRef.current !== requestId) return;

      const data = await response.data;
      const details = Array.isArray(data.product_details) ? data.product_details : [];
      productCatalogRef.current = details.map((item) => ({
        id: item.id,
        product_id: item.product_id ?? item.id,
        product_name: item.product_name,
        product_code: item.product_code,
        split: item.split,
        pack: item.pack,
        noOfPieceInBox: item.noOfPieceInBox,
        pack_size: item.pack_size,
        unit_per_box: item.unit_per_box,
      }));
      const formattedOptions = productCatalogRef.current.map((item) => ({
        label: `${item.product_name || 'N/A'} - ${item.product_code || 'N/A'}`,
        value: String(item.product_id),
      }));
      productOptionsRef.current = formattedOptions;
      setProductOptions(formattedOptions.slice(0, PRODUCT_OPTION_INITIAL));
    } catch (error) {
      if (productListRequestRef.current === requestId) {
        toast.error("Error fetching products data");
      }
    }
  };

  const fetchReturnDetailRows = async (customerId, extraParams = {}) => {
    const formData = new FormData();
    formData.append('customer_id', customerId);
    Object.entries(extraParams).forEach(([key, value]) => {
      if (value != null && value !== '') {
        formData.append(key, value);
      }
    });

    const response = await fetch(`${config.API_URL}/get_return_datails`, {
      method: 'POST',
      headers: {
        authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    if (isReturnDetailsUnavailable(data)) {
      return [];
    }

    return normalizeReturnDetailsRows(data).map((row) =>
      mapReturnDetailRow(row, customerId)
    );
  };

  const fetchReturnItemsByOrder = async (customerId, orderOption) => {
    const referenceNo = String(orderOption?.reference_no ?? '').trim();
    const saleId = orderOption?.sale_id;
    const cacheKey = `${customerId}:${saleId}:${referenceNo}`;

    if (returnDetailsByInvoiceRef.current.has(cacheKey)) {
      return returnDetailsByInvoiceRef.current.get(cacheKey);
    }

    const params = {};
    if (saleId != null && saleId !== '') {
      params.sale_id = saleId;
    }
    if (referenceNo) {
      params.reference_no = referenceNo;
    }

    const rows = await fetchReturnDetailRows(customerId, params);
    const filtered = referenceNo
      ? rows.filter((row) => String(row.reference_no) === String(referenceNo))
      : rows;

    returnDetailsByInvoiceRef.current.set(cacheKey, filtered);
    return filtered;
  };

  const addRowsToTempReturn = async (rows, reason) => {
    const currentTempRows = returnTableRowsRef.current;
    const formData = new FormData();
    const submittedRows = [];

    for (const row of rows) {
      const effectiveReturnType = resolveEffectiveReturnType(
        row,
        normalizeReturnUnit(row.order_type)
      );
      const maxReturnQty = getMaxReturnQtyInUnit(
        row,
        effectiveReturnType,
        currentTempRows,
        selectedCustomer?.value
      );
      if (maxReturnQty <= 0.0001) {
        continue;
      }
      formData.append('order_ids[]', row.id);
      formData.append('return_qty[]', maxReturnQty);
      formData.append('return_type[]', effectiveReturnType);
      formData.append('returnReason[]', reason);
      submittedRows.push(row);
    }

    if (submittedRows.length === 0) {
      toast.error('All items from this invoice are already returned');
      return false;
    }

    formData.append('user_id', user_id);
    formData.append('customer_id', selectedCustomer.value);

    const response = await fetch(`${config.API_URL}/add_temp_return`, {
      method: 'POST',
      headers: {
        authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to add return');
    }

    returnDetailsByInvoiceRef.current.clear();
    await fetchReturnProductInfo(selectedCustomer);
    return submittedRows.length;
  };

  const handleOrderChange = async (orderOption) => {
    setSelectedOrder(orderOption);
    if (!orderOption) {
      return;
    }
    if (!selectedCustomer?.value) {
      toast.error('Please select a customer first');
      setSelectedOrder(null);
      return;
    }
    if (!bulkReturnReason) {
      toast.error('Please select return reason first');
      setSelectedOrder(null);
      return;
    }

    const requestId = orderReturnRequestRef.current + 1;
    orderReturnRequestRef.current = requestId;
    setIsLoadingOrderReturn(true);

    try {
      const rows = await fetchReturnItemsByOrder(selectedCustomer.value, orderOption);
      if (orderReturnRequestRef.current !== requestId) {
        return;
      }
      if (!rows.length) {
        toast.error(`No returnable items found for invoice ${orderOption.reference_no || ''}`);
        return;
      }

      const addedCount = await addRowsToTempReturn(rows, bulkReturnReason);
      if (orderReturnRequestRef.current !== requestId) {
        return;
      }
      if (addedCount) {
        toast.success(
          `Added ${addedCount} item(s) from invoice ${orderOption.reference_no || orderOption.label}`
        );
        setSelectedOrder(null);
        setOrderInputValue('');
      }
    } catch (error) {
      if (orderReturnRequestRef.current === requestId) {
        toast.error('Failed to add invoice items to return list');
      }
    } finally {
      if (orderReturnRequestRef.current === requestId) {
        setIsLoadingOrderReturn(false);
      }
    }
  };

  const productHandleChange = (selectedOption) => {
    if (selectedOption) {
      setProductValue(selectedOption);
      setProductInputValue(selectedOption.label || '');
      setProductOptions((prev) => {
        const exists = prev.some((o) => String(o.value) === String(selectedOption.value));
        if (exists) return prev;
        return [selectedOption, ...prev].slice(0, PRODUCT_OPTION_LIMIT);
      });
      const rawProduct = productCatalogRef.current.find(
        (item) => String(item.product_id ?? item.id) === String(selectedOption.value)
      );
      if (rawProduct && selectedCustomer?.value) {
        productSplitService.storeSplitData(
          selectedCustomer.value,
          rawProduct.product_id ?? rawProduct.id,
          rawProduct
        );
      }
      fetchAddProductInfo(selectedOption.value, selectedOption.label);
    } else {
      setProductValue(null);
      setProductInputValue('');
      setProductOptions(productOptionsRef.current.slice(0, PRODUCT_OPTION_INITIAL));
    }
  };

  const fetchAddProductInfo = async (productId, productLabel = '') => {
    const unavailableMessage = productLabel
      ? `Data is not available for return: ${productLabel}`
      : 'Data is not available for return';
    try {
      const rows = await fetchReturnDetailRows(selectedCustomer.value, {
        product_id: productId,
      });
      if (!rows.length) {
        toast.error(unavailableMessage);
        return;
      }

      setTableRows((prevRows) => {
        const existingIds = new Set(prevRows.map((r) => String(r.id)));
        const toAdd = rows.filter((r) => !existingIds.has(String(r.id)));
        if (toAdd.length === 0) {
          toast.error(
            productLabel
              ? `Order items for this product are already added: ${productLabel}`
              : 'Order items for this product are already added'
          );
          return prevRows;
        }
        return [...prevRows, ...toAdd];
      });
    } catch (error) {
      toast.error(unavailableMessage);
    }
  };

  const handleCheckboxChange = (e) => {
    const value = e.target.value;
    const isChecked = e.target.checked;

    setCheckedValues((prevCheckedValues) => {
      if (isChecked) {
        return [...prevCheckedValues, value];
      }
      return prevCheckedValues.filter((val) => val !== value);
    });
  };

  const handleReturnSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCustomer) {
      toast.error('Please select a customer.');
      return;
    }
    if (checkedValues.length === 0) {
      toast.error('Please select at least one item to return.');
      return;
    }

    const formData = new FormData();
    const invalidRows = [];
    const submittedRows = [];
    const newQtyErrors = {};
    const newReasonErrors = {};

    for (const row of tableRows) {
      if (!checkedValues.includes(String(row.id))) continue;

      const selectedReturnType =
        returnTypeValues[row.id] ?? normalizeReturnUnit(row.order_type);
      const qtyRaw = returnQtyValues[row.id] ?? '';
      const returnQty = parseFloat(String(qtyRaw).trim());
      const reason = returnReasonValues[row.id] ?? '';

      let valid = true;
      if (String(qtyRaw).trim() === '' || Number.isNaN(returnQty) || returnQty < 0) {
        newQtyErrors[row.id] = 'Enter quantity (min 0)';
        valid = false;
      }
      if (!reason || reason === '') {
        newReasonErrors[row.id] = 'Select reason';
        valid = false;
      }

      if (!valid) continue;

      const effectiveReturnType = resolveEffectiveReturnType(row, selectedReturnType);
      const maxReturnQty = getMaxReturnQtyInUnit(
        row,
        effectiveReturnType,
        returnTableRows,
        selectedCustomer?.value
      );

      if (returnQty > maxReturnQty + 1e-9) {
        invalidRows.push(row);
      } else {
        formData.append('order_ids[]', row.id);
        formData.append('return_qty[]', returnQty);
        formData.append('return_type[]', effectiveReturnType);
        formData.append('returnReason[]', reason);
        submittedRows.push(row);
      }
    }

    setReturnQtyErrors(newQtyErrors);
    setReturnReasonErrors(newReasonErrors);

    const hasFieldErrors =
      Object.keys(newQtyErrors).length > 0 || Object.keys(newReasonErrors).length > 0;
    if (hasFieldErrors) {
      toast.error('Please enter valid return quantity and select a reason for each selected item.');
      return;
    }
    if (invalidRows.length > 0) {
      toast.error(
        `Return quantity exceeds allowed limit for: ${invalidRows.map((row) => row.reference_no).join(', ')}`
      );
      return;
    }
    if (submittedRows.length === 0) {
      return;
    }

    try {
      formData.append('user_id', user_id);
      formData.append('customer_id', selectedCustomer.value);
      const response = await fetch(`${config.API_URL}/add_temp_return`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to add return');
      }

      toast.success('Return added successfully!');

      const remainingRows = tableRows.filter(
        (row) => !submittedRows.some((s) => s.id === row.id)
      );
      setTableRows(remainingRows);
      setCheckedValues((prev) =>
        prev.filter((id) => !submittedRows.some((s) => String(s.id) === id))
      );
      const nextQty = { ...returnQtyValues };
      const nextReason = { ...returnReasonValues };
      submittedRows.forEach((r) => {
        delete nextQty[r.id];
        delete nextReason[r.id];
      });
      setReturnQtyValues(nextQty);
      setReturnReasonValues(nextReason);
      setReturnQtyErrors({});
      setReturnReasonErrors({});
      setProductValue(null);
      setProductInputValue('');
      fetchReturnProductInfo(selectedCustomer);
    } catch (error) {
      toast.error('Something went wrong while submitting the return.');
    }
  };

  const deleteReturnRecord = (rowId) => {
    setConfirmDialog({
      open: true,
      title: 'Are you sure?',
      message: 'Do you want to delete this return record?',
      onConfirm: async () => {
        setConfirmDialog({ open: false, title: "", message: "", onConfirm: null });
        const formData = new FormData();
        formData.append('sale_item_id', rowId);

        const apiUrl = `${config.API_URL}/remove_return_item`;

        try {
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              authorization: `Bearer ${token}`,
            },
            body: formData,
          });

          if (!response.ok) {
            throw new Error('Failed to delete return record');
          }

          toast.success('The return record has been deleted.');
          returnDetailsByInvoiceRef.current.clear();
          fetchReturnProductInfo(selectedCustomer);
        } catch (error) {
          toast.error('There was an issue deleting the return record.');
        }
      },
    });
  };

  const fetchReturnProductInfo = async (selectedOption) => {
    const formData = new FormData();
    formData.append('customer_id', selectedOption.value);
    const apiUrl = `${config.API_URL}/add_temp_return`;
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch new product');
      }

      const data = await response.json();

      setReturnTableRows(data.map(productData => ({
        id: productData.id,
        sale_item_id: productData.sale_item_id ?? productData.order_id,
        order_line_id: productData.order_line_id ?? productData.sale_item_id ?? productData.order_id,
        reference_no: productData.reference_no,
        invoice_date: productData.invoice_date,
        quantity: productData.quantity,
        product_code: productData.product_code,
        product_id: productData.product_id,
        product_name: productData.product_name,
        unit_price: productData.unit_price,
        order_type: productData.order_type,
        item_tax: productData.item_tax,
        pack: productData.pack,
        noOfPieceInBox: productData.noOfPieceInBox ?? productData.pack_size,
        return_qty: productData.return_qty,
        subtotal: productData.subtotal,
        return_type: productData.return_type,
        return_unitprice: productData.return_unitprice,
        return_itemtax: productData.return_itemtax,
        return_total: productData.return_total,
        returnReason: productData.returnReason,
      })));
    } catch (error) {
      toast.error('Error fetching return product info');
    }
  };

  const SubmitRejectFormData = () => {
    if (!selectedDate || returnTableRows.length === 0) {
      toast.error('Please select a date and add products to return.');
      return;
    }

    setConfirmDialog({
      open: true,
      title: 'Are you sure?',
      message: 'Do you want to submit this return?',
      onConfirm: async () => {
        setConfirmDialog({ open: false, title: "", message: "", onConfirm: null });

        const formData = {
          return_date: selectedDate,
          customerId: selectedCustomer.value,
          note: noteInfo,
          user_id: user_id,
          returnProductDetails: returnTableRows.map((row) => ({
            itemId: row.id.toString(),
            returnPieceType: row.return_type,
            returnQty: row.return_qty.toString(),
            returnReason: row.returnReason || '',
          })),
        };

        try {
          const apiUrl = `${config.API_URL}/return_request_web`;
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(formData),
          });

          if (!response.ok) {
            throw new Error('Failed to submit return');
          }

          const data = await response.json();
          toast.success('Return data submitted successfully!');

          setSelectedDate('');
          setReturnTableRows([]);
          navigate('/return_list');
        } catch (error) {
          toast.error('Something went wrong. Please try again.');
        }
      },
    });

      
  };
  return (
    <Box>
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py:1.2}}>
            <form id='addcustomerform'>
              <Grid2 container spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Grid2 size={{ xs: 12, sm: 6, md: 1.4 }}>
                  <Typography className="page-title" sx={{ whiteSpace: 'nowrap' }}>
                    Add Return
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 6, sm: 4, md: 1.4 }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      className="input-border"
                      label="Date"
                      value={getDateValue()}
                      onChange={handleDateChange}
                      format="dd-MM-yyyy"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          sx: {
                            minWidth: 0,
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#f5f7fa",
                              fontSize: "0.8125rem",
                              "&:hover": { backgroundColor: "#fff" },
                              "&.Mui-focused": { backgroundColor: "#fff" },
                            },
                            "& .MuiInputLabel-root": {
                              color: "#000",
                              fontSize: "0.8125rem",
                            },
                            '& .MuiFormLabel-root': { top: '-3px' },
                            '& .MuiFormLabel-root.Mui-focused': { top: '0px' },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid2>
                <Grid2 size={{ xs: 6, sm: 8, md: 2.2 }}>
                  <Autocomplete
                    className="input-border"
                    options={customerData}
                    value={selectedCustomer}
                    onChange={(event, newValue) => handleCustomerChange(newValue)}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === 'reset') return;
                      handleCustomerInputChange(newInputValue);
                    }}
                    getOptionLabel={(option) => option?.label || ''}
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Customer"
                        size="small"
                        className="input-border"
                      />
                    )}
                    sx={{
                      minWidth: 0,
                      '& .MuiAutocomplete-inputRoot': {
                        flexWrap: 'nowrap',
                      },
                      '& .MuiAutocomplete-input': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                      '& .MuiAutocomplete-popper': { zIndex: 9999 },
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 6, sm: 4, md: 1.3 }}>
                  <FormControl fullWidth size="small" className="input-border" sx={{ minWidth: 0 }}>
                    <InputLabel id="return-add-mode-label">Add By</InputLabel>
                    <MuiSelect
                      labelId="return-add-mode-label"
                      label="Add By"
                      value={returnAddMode}
                      onChange={(e) => handleReturnAddModeChange(e.target.value)}
                    >
                      <MenuItem value="invoice">Invoice</MenuItem>
                      <MenuItem value="product">Product</MenuItem>
                    </MuiSelect>
                  </FormControl>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 8, md: returnAddMode === 'invoice' ? 3.4 : 5.7 }}>
                  {returnAddMode === 'invoice' ? (
                    <Autocomplete
                      className="input-border"
                      options={orderOptions}
                      value={selectedOrder}
                      inputValue={orderInputValue}
                      loading={isLoadingOrderReturn}
                      onInputChange={(event, newInputValue, reason) => {
                        if (reason === 'reset') {
                          setOrderInputValue(newInputValue);
                          return;
                        }
                        if (reason === 'clear') {
                          setSelectedOrder(null);
                          setOrderInputValue('');
                          setOrderOptions(allOrderOptions.slice(0, ORDER_OPTION_LIMIT));
                          return;
                        }
                        setOrderInputValue(newInputValue);
                        filterOrderOptions(newInputValue);
                      }}
                      onChange={(event, newValue) => handleOrderChange(newValue)}
                      onOpen={() => {
                        if (allOrderOptions.length && orderOptions.length === 0) {
                          setOrderOptions(allOrderOptions.slice(0, ORDER_OPTION_LIMIT));
                        }
                      }}
                      getOptionLabel={(option) => option?.label || ''}
                      isOptionEqualToValue={(option, value) =>
                        String(option?.value) === String(value?.value)
                      }
                      filterOptions={(options) => options}
                      ListboxProps={{ style: { maxHeight: 280 } }}
                      disableClearable={false}
                      disabled={!selectedCustomer || isLoadingOrderReturn}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Invoice"
                          size="small"
                          className="input-border"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {isLoadingOrderReturn ? (
                                  <CircularProgress color="inherit" size={18} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      sx={{
                        minWidth: 0,
                        '& .MuiAutocomplete-popper': { zIndex: 9999 },
                      }}
                    />
                  ) : (
                    <Autocomplete
                      className="input-border"
                      options={productOptions}
                      value={productValue}
                      inputValue={productInputValue}
                      onInputChange={(event, newInputValue, reason) => {
                        if (reason === 'reset') {
                          setProductInputValue(newInputValue);
                          return;
                        }
                        if (reason === 'clear') {
                          setProductValue(null);
                          setProductInputValue('');
                          setProductOptions(productOptionsRef.current.slice(0, PRODUCT_OPTION_INITIAL));
                          return;
                        }
                        setProductInputValue(newInputValue);
                        filterProductOptions(newInputValue);
                      }}
                      onChange={(event, newValue) => productHandleChange(newValue)}
                      onOpen={() => {
                        if (productOptionsRef.current.length && productOptions.length === 0) {
                          setProductOptions(productOptionsRef.current.slice(0, PRODUCT_OPTION_INITIAL));
                        }
                      }}
                      getOptionLabel={(option) => option?.label || ''}
                      isOptionEqualToValue={(option, value) =>
                        String(option?.value) === String(value?.value)
                      }
                      filterOptions={(options) => options}
                      ListboxProps={{ style: { maxHeight: 280 } }}
                      disableClearable={false}
                      disabled={!selectedCustomer}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Product"
                          size="small"
                          className="input-border"
                        />
                      )}
                      sx={{
                        minWidth: 0,
                        '& .MuiAutocomplete-popper': { zIndex: 9999 },
                      }}
                    />
                  )}
                </Grid2>
                {returnAddMode === 'invoice' ? (
                  <Grid2 size={{ xs: 6, sm: 4, md: 1.3 }}>
                    <FormControl fullWidth size="small" className="input-border" sx={{ minWidth: 0 }}>
                      <InputLabel id="bulk-return-reason-label">Return Reason</InputLabel>
                      <MuiSelect
                        labelId="bulk-return-reason-label"
                        label="Return Reason"
                        value={bulkReturnReason}
                        onChange={(e) => setBulkReturnReason(e.target.value)}
                      >
                        <MenuItem value="damaged">Damaged</MenuItem>
                        <MenuItem value="expired">Expired</MenuItem>
                        <MenuItem value="unwanted">Unwanted</MenuItem>
                      </MuiSelect>
                    </FormControl>
                  </Grid2>
                ) : null}
              </Grid2>

              {returnAddMode === 'product' ? (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.75rem' }}>
                    Product mode: select lines below, enter qty and reason, then click Add Return.
                  </Typography>
                </Box>
              ) : null}

              {returnAddMode === 'product' ? (
                <>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      lineHeight: 1.5,
                      color: "#212b36",
                    }}
                  >
                    Order Items
                  </Typography>
                  <TableContainer sx={{ maxHeight: 400, border: "1px solid rgba(145, 158, 171, 0.16)", borderRadius: 1 }}>
                    <Table
                      size="medium"
                      className="table-cell-border"
                      sx={{ tableLayout: "fixed"}}
                    >
                      <TableHead>
                        <TableRow className="table-cell-header">
                          <TableCell width="120px"></TableCell>
                          <TableCell width="120px">Invoice Date</TableCell>
                          <TableCell width="120px">Invoice No</TableCell>
                          <TableCell width="120px">Order Qty</TableCell>
                          <TableCell width="120px">Total Price</TableCell>
                          <TableCell width="120px">{company_tax_type}</TableCell>
                          <TableCell width="120px">Sub Total</TableCell>
                          <TableCell width="120px">Return Qty</TableCell>
                          <TableCell width="120px">Return Type</TableCell>
                          <TableCell width="120px">Reason</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tableRows.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#637381",
                                  fontSize: "0.75rem",
                                  lineHeight: 1.5,
                                }}
                              >
                                No product
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          tableRows.map((row) => (
                            <TableRow
                              key={row.id}
                              hover
                              className="table-cell-style"
                            >
                              <TableCell>
                                <Checkbox
                                  checked={checkedValues.includes(String(row.id))}
                                  onChange={handleCheckboxChange}
                                  value={row.id}
                                  size="small"
                                  className="checkBox"
                                />
                              </TableCell>
                              <TableCell>{row.invoice_date}</TableCell>
                              <TableCell>{row.reference_no}</TableCell>
                              <TableCell>{parseInt(row.quantity)}({row.order_type})</TableCell>
                              <TableCell>{row.unit_price}</TableCell>
                              <TableCell>{row.item_tax}</TableCell>
                              <TableCell>{row.subtotal}</TableCell>
                              <TableCell>
                                <TextField
                                  type="number"
                                  className="input-border"
                                  name="return_qty[]"
                                  data-id={row.id}
                                  size="small"
                                  value={returnQtyValues[row.id] ?? ''}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setReturnQtyValues((prev) => ({ ...prev, [row.id]: v }));
                                    setReturnQtyErrors((prev) => ({ ...prev, [row.id]: '' }));
                                  }}
                                  error={!!returnQtyErrors[row.id]}
                                  helperText={returnQtyErrors[row.id]}
                                  inputProps={{
                                    min: 0,
                                    style: {
                                      textAlign: 'center',
                                      width: '70px',
                                      fontSize: '0.75rem',
                                    }
                                  }}
                                  sx={{
                                    width: '80px',
                                    "& .MuiOutlinedInput-root": {
                                      fontSize: "0.75rem",
                                    },
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ py: 1 }}>
                                {row.split === 2 || row.split === 0 || row.split === "2" || row.split === "0" ? (
                                  <FormControl size="small" fullWidth>
                                    <MuiSelect
                                      name="return_type[]"
                                      data-id={row.id}
                                      value={
                                        returnTypeValues[row.id] ??
                                        normalizeReturnUnit(row.order_type)
                                      }
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        setReturnTypeValues((prev) => ({ ...prev, [row.id]: v }));
                                      }}
                                      sx={{
                                        fontSize: "0.75rem",
                                        "& .MuiSelect-select": {
                                          fontSize: "0.75rem",
                                        },
                                      }}
                                    >
                                      <MenuItem value="box" sx={{ fontSize: "0.75rem" }}>Box</MenuItem>
                                      <MenuItem value="piece" sx={{ fontSize: "0.75rem" }}>Piece</MenuItem>
                                    </MuiSelect>
                                  </FormControl>
                                ) : (
                                  <Typography sx={{ fontSize: "0.75rem" }}>
                                    {row.split === 1 || row.split === "1" ? "Piece" : "Box"}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ py: 1 }}>
                                <FormControl size="small" fullWidth error={!!returnReasonErrors[row.id]}>
                                  <MuiSelect
                                    className="input-border"
                                    name="returnReason[]"
                                    data-id={row.id}
                                    value={returnReasonValues[row.id] ?? ''}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      setReturnReasonValues((prev) => ({ ...prev, [row.id]: v }));
                                      setReturnReasonErrors((prev) => ({ ...prev, [row.id]: '' }));
                                    }}
                                    displayEmpty
                                    sx={{
                                      fontSize: "0.75rem",
                                      "& .MuiSelect-select": {
                                        fontSize: "0.75rem",
                                      },
                                    }}
                                  >
                                    <MenuItem value="" sx={{ fontSize: "0.75rem" }}>Select</MenuItem>
                                    <MenuItem value="damaged" sx={{ fontSize: "0.75rem" }}>Damaged</MenuItem>
                                    <MenuItem value="expired" sx={{ fontSize: "0.75rem" }}>Expired</MenuItem>
                                    <MenuItem value="unwanted" sx={{ fontSize: "0.75rem" }}>Unwanted</MenuItem>
                                  </MuiSelect>
                                  {returnReasonErrors[row.id] && (
                                    <FormHelperText error sx={{ fontSize: '0.75rem' }}>
                                      {returnReasonErrors[row.id]}
                                    </FormHelperText>
                                  )}
                                </FormControl>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1, mb: 1 }}>
                    <Button
                      variant="contained"
                      onClick={handleReturnSubmit}
                      size="small"
                      className="button-variant-contained"
                    >
                      Add Return
                    </Button>
                  </Box>
                </>
              ) : null}

              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  color: "#212b36",
                  mt: returnAddMode === 'product' ? 1 : 0,
                }}
              >
                Return Items
              </Typography>
              <TableContainer sx={{ mb: 3, maxHeight: 400, border: "1px solid rgba(145, 158, 171, 0.16)", borderRadius: 1 }}>
                <Table
                  size="medium"
                  className="table-cell-border"
                  sx={{ tableLayout: "fixed"}}
                >
                  <TableHead>
                    <TableRow className="table-cell-header">
                      <TableCell width="120px" >Invoice Date</TableCell>
                      <TableCell width="100px" >Invoice No</TableCell>
                      <TableCell width="150px" >Product Name</TableCell>
                      <TableCell >Qty</TableCell>
                      <TableCell >Price</TableCell>
                      <TableCell >{company_tax_type}</TableCell>
                      <TableCell >Total</TableCell>
                      <TableCell width="120px" >Return Qty</TableCell>
                      <TableCell >Amt</TableCell>
                      <TableCell >{company_tax_type}</TableCell>
                      <TableCell >Subtotal</TableCell>
                      <TableCell >Reason</TableCell>
                      <TableCell >Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {returnTableRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#637381",
                              fontSize: "0.75rem",
                              lineHeight: 1.5,
                            }}
                          >
                            No product
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      returnTableRows.map((row) => (
                        <TableRow
                          key={row.id}
                          hover
                          className="table-cell-style"
                        >
                          <TableCell >{row.invoice_date}</TableCell>
                          <TableCell >{row.reference_no}</TableCell>
                          <TableCell >{row.product_code} - {row.product_name}</TableCell>
                          <TableCell >{parseInt(row.quantity)}({row.order_type})</TableCell>
                          <TableCell >{parseFloat(row.unit_price).toFixed(2)}</TableCell>
                          <TableCell >{parseFloat(row.item_tax).toFixed(2)}</TableCell>
                          <TableCell >{parseFloat(row.subtotal).toFixed(2)}</TableCell>
                          <TableCell >{parseInt(row.return_qty)}({row.return_type})</TableCell>
                          <TableCell >{parseFloat(row.return_unitprice).toFixed(2)}</TableCell>
                          <TableCell >{parseFloat(row.return_itemtax).toFixed(2)}</TableCell>
                          <TableCell >{parseFloat(row.return_total).toFixed(2)}</TableCell>
                          <TableCell >{row.returnReason}</TableCell>
                          <TableCell >
                            <IconButton
                              size="small"
                              onClick={() => deleteReturnRecord(row.id)}
                              sx={{
                                color: "#ff5630",
                                "&:hover": {
                                  backgroundColor: "rgba(255, 86, 48, 0.08)",
                                },
                              }}
                            >
                              <Iconify icon="eva:trash-2-outline" width={18} height={18} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Grid2 container spacing={2} sx={{ mb:1}}>
                <Grid2 size={{ xs:6 }}>
                  <TextField
                    className="form-input-border"
                    fullWidth
                    label="Note"
                    multiline
                    rows={2}
                    border="1px solid rgba(35, 36, 37, 0.62)"
                    value={noteInfo}
                    onChange={handleNoteChange}
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        fontSize: "0.875rem",
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.875rem",
                      },
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs:6 }}>
                <Box sx={{ display: "flex", justifyContent: "flex-end"}}>
                <Button
                  variant="contained"
                  className="button-variant-contained"
                  onClick={SubmitRejectFormData}
                  size="small"
                >
                  Submit
                </Button>
              </Box>
                </Grid2>
              </Grid2>

        
            </form>
          </Box>
        </Paper>
      </Box>
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, title: "", message: "", onConfirm: null })}
      >
        <DialogTitle
          sx={{
            fontSize: "1.125rem",
            fontWeight: 600,
            color: "#212b36",
          }}
        >
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              fontSize: "0.875rem",
              color: "#637381",
              lineHeight: 1.5,
            }}
          >
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, title: "", message: "", onConfirm: null })}
            color="inherit"
            sx={{
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.875rem",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (confirmDialog.onConfirm) {
                confirmDialog.onConfirm();
              }
            }}
            variant="contained"
            className="button-variant-contained"
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddReturn;
